import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import { PENDING_CHANGES_SUBTAB } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/PendingChangesDrawer/PendingChangesDrawer.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';

import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';

import {
  getApplicationBorrowersAction,
  getAssetsAction,
  getFinancialInfoPropertiesAction,
  getFinancialInformationSummaryAction,
  getIncomesAction,
  getLiabilitiesAction,
  getPropertiesActionV2,
} from 'store/loanApplications/actions';

import { FINANCIAL_INFO_VIEWS_PATHS } from './FinancialInfo.constants';
import { wrapperStyles } from './FinancialInfo.styles';
import { generateFinancialInfoPath } from './FinancialInfo.utils';
import FinancialInfoBody from './FinancialInfoBody';
import FinancialInfoHead from './FinancialInfoHead';
import UpdateTableModal from './UpdateTableModal';

const FinancialInfo = ({ isLoading, setIsLoading }) => {
  const { applicationId, tab, subtab: tablePath, entityId: borrowerId } = useParams();
  const history = useHistory();

  const [isError, setIsError] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (tablePath === PENDING_CHANGES_SUBTAB) {
      return;
    }

    if (tablePath && Object.values(FINANCIAL_INFO_VIEWS_PATHS).includes(tablePath)) {
      return;
    }

    history.push(
      generateFinancialInfoPath({
        applicationId,
        tab,
        tablePath: FINANCIAL_INFO_VIEWS_PATHS.ALL_VIEWS,
        borrowerId,
      }),
    );
  }, [applicationId, borrowerId, history, tab, tablePath]);

  const fetchData = useCallback(async () => {
    setIsError(false);
    setIsLoading(true);

    Promise.all([
      dispatch(getAssetsAction({ applicationId })),
      dispatch(getLiabilitiesAction({ applicationId })),
      dispatch(getIncomesAction({ applicationId })),
      dispatch(getFinancialInfoPropertiesAction({ applicationId })),
      dispatch(getFinancialInformationSummaryAction({ applicationId })),
      dispatch(getPropertiesActionV2({ applicationId, withSubjectProperty: true })),
      dispatch(getApplicationBorrowersAction({ applicationId })),
    ])
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [applicationId, dispatch, setIsLoading]);

  usePendingChangesDependentFetch(fetchData);

  return (
    <>
      <ErrorHandlingLayout isError={isError} onClick={fetchData}>
        <Box sx={wrapperStyles}>
          <FinancialInfoHead isLoading={isLoading || isError} />
          <FinancialInfoBody isLoading={isLoading || isError} />
        </Box>
      </ErrorHandlingLayout>

      <UpdateTableModal isDataLoading={isLoading} />
    </>
  );
};

FinancialInfo.propTypes = {
  ...WithLoadingPropTypes,
};

export default withLoading(memo(FinancialInfo));
